import Grid from "@mui/material/Grid2";
import { FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Fragment, ReactNode } from "react";
import StaffelSelect, { StaffelSelectOption, StaffelSelectType } from "view/staffels/components/selects/StaffelSelect";
import usePopover from "components/hooks/usePopover";
import { Box, IconButton, Popover } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { StaffelUnit } from "__generated__/graphql";
import FormField from "components/cck/FormField";
import { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";
import FeetStaffelItemsTable from "view/staffels/components/tables/FeetStaffelItemsTable";
import GasolineStaffelItemsTable from "view/staffels/components/tables/GasolineStaffelItemsTable";
import WaterLevelStaffelItemsTable from "view/staffels/components/tables/WaterLevelStaffelItemsTable";

type FormValues = {
	agreements: {
		calculationMethod: {
			staffelUnit?: StaffelUnit;
			staffel?: StaffelSelectOption;
		}
	}[];
}

type FormField = {
	id: string;
	render: () => ReactNode;
}

const StaffelDiscriminatorByUnit = {
	[StaffelUnit.Feet]: StaffelSelectType.FeetStaffel,
	[StaffelUnit.Gasoline]: StaffelSelectType.GasolineStaffel,
	[StaffelUnit.WaterLevel]: StaffelSelectType.WaterLevelStaffel,
};

const StaffelCalculationMethodForm = ({ hiddenFields, index }: CalculationMethodFormByTypeProps) => {
	const { control, formState: { errors } } = useFormContext<FormValues>();

	const staffelUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.staffelUnit` });

	const StaffelCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.staffel",
			render: () => staffelUnit && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.staffel`}>
						<FormGroup label={intl.formatMessage({ id: "STAFFEL" })} required>
							<Controller
								name={`agreements.${index}.calculationMethod.staffel`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<div style={{ display: "flex" }}>
										<div style={{ flexGrow: 1 }}>
											<StaffelSelect type={StaffelDiscriminatorByUnit[staffelUnit]} {...field} />
										</div>

										{field.value && (
											<StaffelIcon staffel={field.value} />
										)}
									</div>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	return (
		<>
			{StaffelCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

const StaffelIcon = ({ staffel } : { staffel?: StaffelSelectOption }) => {
	const { popoverProps, onPopoverOpen, onPopoverClose } = usePopover({
		trigger: "hover",
	});

	return (
		<>
			<Popover {...popoverProps}>
				<Box sx={{ width: "600px" }} p={2}>
					{staffel?.discriminator == "FeetStaffel" && (
						<FeetStaffelItemsTable content={staffel?.staffelItems ?? []} />
					)}

					{staffel?.discriminator == "GasolineStaffel" && (
						<GasolineStaffelItemsTable content={staffel?.staffelItems ?? []} />
					)}

					{staffel?.discriminator == "WaterLevelStaffel" && (
						<WaterLevelStaffelItemsTable content={staffel?.staffelItems ?? []} />
					)}
				</Box>
			</Popover>

			<IconButton size="small" onMouseEnter={onPopoverOpen} onMouseLeave={onPopoverClose}>
				<AssignmentIcon fontSize="small" />
			</IconButton>
		</>
	);
};

export default StaffelCalculationMethodForm;
